import { CompanionAd } from '@quickplay/player-interface';
import { CompanionSize } from './CompanionTypes';

/** Selects the largest declared slot size that fits the app's container. */
export function selectStaticCompanion(
  companions: CompanionAd[],
  sizes: CompanionSize[],
  containerWidth: number,
  containerHeight = Infinity,
): CompanionAd | undefined {
  let selected: CompanionAd | undefined;
  for (const companion of companions) {
    if (
      !Number.isFinite(companion.width) ||
      !Number.isFinite(companion.height) ||
      companion.width <= 0 ||
      companion.height <= 0 ||
      !(companion.width <= containerWidth && companion.height <= containerHeight) ||
      !sizes.some(size => size.width === companion.width && size.height === companion.height)
    ) {
      continue;
    }
    if (!selected || companion.width * companion.height > selected.width * selected.height) {
      selected = companion;
    }
  }
  return selected;
}

export interface StaticCompanionCallbacks {
  onLoaded(): void;
  onClick(): void;
  onFailed(): void;
}

/** Renders a static image. The presentation owns visibility, navigation and tracking pixels. */
export class StaticCompanionView {
  readonly element: HTMLElement;
  private readonly image: HTMLImageElement;
  private disposed = false;
  private settled = false;
  private loaded = false;

  constructor(companion: CompanionAd, callbacks: StaticCompanionCallbacks) {
    this.element = document.createElement(companion.clickThroughUrl ? 'button' : 'div');
    this.element.dataset.companion = 'static';
    Object.assign(this.element.style, {
      display: 'block',
      width: `${companion.width}px`,
      height: `${companion.height}px`,
      padding: '0',
      border: '0',
      background: 'transparent',
      cursor: companion.clickThroughUrl ? 'pointer' : 'default',
    });
    if (this.element instanceof HTMLButtonElement) {
      this.element.type = 'button';
      this.element.setAttribute('aria-label', 'Advertisement');
    }

    this.image = document.createElement('img');
    this.image.alt = 'Advertisement';
    this.image.width = companion.width;
    this.image.height = companion.height;
    Object.assign(this.image.style, { display: 'block', width: '100%', height: '100%', objectFit: 'contain' });
    this.image.onload = () => {
      if (this.disposed || this.settled) {
        return;
      }
      this.settled = true;
      this.loaded = true;
      callbacks.onLoaded();
    };
    this.image.onerror = () => {
      if (this.disposed || this.settled) {
        return;
      }
      this.settled = true;
      callbacks.onFailed();
    };
    this.element.onclick = () => {
      if (!this.disposed && this.loaded && companion.clickThroughUrl) {
        callbacks.onClick();
      }
    };
    this.element.append(this.image);
    this.image.src = companion.staticResource;
  }

  dispose(): void {
    this.disposed = true;
    if (this.element instanceof HTMLButtonElement) {
      this.element.disabled = true;
    }
    this.image.onload = null;
    this.image.onerror = null;
    this.element.onclick = null;
    this.image.removeAttribute('src');
    this.element.remove();
  }
}
